import { useCallback, useEffect, useState } from 'react';
import { leadsApi } from '@/features/leads/api/leadsApi';
import type { LeadDto } from '@/shared/types/domain';
import { ApiError } from '@/shared/api/httpClient';

interface UseLeadsResult {
  leads: LeadDto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useLeads(exhibitorId: string | undefined): UseLeadsResult {
  const [leads, setLeads] = useState<LeadDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  useEffect(() => {
    if (!exhibitorId) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    leadsApi
      .getByExhibitor(exhibitorId, controller.signal)
      .then(setLeads)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.userMessage : 'Could not load leads.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [exhibitorId, refetchTick]);

  const refetch = useCallback(() => setRefetchTick((t) => t + 1), []);

  return { leads, isLoading, error, refetch };
}
